import { useMemo, useState, useCallback } from 'react'
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@you/ui'
import { Search, Plus } from 'lucide-react'
import KnowledgeCard from './KnowledgeCard'
import { useKnowledge } from '@/api/hooks/useKnowledge'
import { KnowledgeStatus, KnowledgeBase } from '@/api/types/knowledge.types'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import EmptyState from '@/components/common/EmptyState'
import UpdateKnowledgeDialog from './UpdateKnowledgeDialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useDebounce } from '@/hooks/useDebounce'

const KnowledgeList = () => {
  const [selectedCreator, setSelectedCreator] = useState('all')
  const [keyword, setKeyword] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editKnowledge, setEditKnowledge] =
    useState<Partial<KnowledgeBase> | null>(null)

  // 获取当前工作空间
  const { currentWorkspace } = useWorkspaceStore()

  // 构建查询参数 - 使用 useMemo 缓存，避免重复创建对象
  const queryParams = useMemo(
    () => ({
      workspaceId: currentWorkspace?.id || '',
      search: keyword || '',
    }),
    [currentWorkspace?.id, keyword]
  )

  // 使用知识库API hook
  const {
    knowledgeList,
    isLoading,
    removeKnowledge,
    toggleKnowledgeStatus,
    isDeleting,
    isToggling,
    refresh,
  } = useKnowledge(queryParams)

  // 如果没有工作区ID，显示加载状态
  const showLoading = !currentWorkspace?.id || isLoading

  // 创建人数据
  const creators = useMemo(() => {
    const uniqueCreators: { id: string; name: string }[] = []
    const seen = new Set<string>()

    for (const item of knowledgeList) {
      if (item.creator && !seen.has(item.creator)) {
        uniqueCreators.push({ id: item.creator, name: item.creator })
        seen.add(item.creator)
      }
    }

    return [{ id: 'all', name: '所有知识库' }, ...uniqueCreators]
  }, [knowledgeList])

  // 筛选知识库列表（按创建人）
  const filteredKnowledgeList = useMemo(() => {
    if (selectedCreator === 'all') return knowledgeList
    return knowledgeList.filter(
      (item) =>
        item.creator === creators.find((c) => c.id === selectedCreator)?.name
    )
  }, [knowledgeList, selectedCreator, creators])

  // 处理新建知识库
  const handleCreateKnowledge = useCallback(() => {
    setDialogOpen(true)
  }, [])

  // 处理编辑知识库
  const handleEditKnowledge = useCallback((knowledge: KnowledgeBase) => {
    setEditKnowledge(knowledge)
    setDialogOpen(true)
  }, [])

  // 处理删除知识库
  const handleDeleteKnowledge = useCallback(
    async (id: string) => {
      try {
        await removeKnowledge(id)
        toast.success('知识库删除成功')
      } catch (error) {
        console.error('删除知识库失败:', error)
        toast.error('删除知识库失败')
      }
    },
    [removeKnowledge]
  )

  // 处理切换知识库状态
  const handleToggleStatus = useCallback(
    async (id: string, status: KnowledgeStatus) => {
      try {
        await toggleKnowledgeStatus(id, status)
        toast.success('知识库状态切换成功')
      } catch (error) {
        toast.error('切换知识库状态失败')
        console.error('切换知识库状态失败:', error)
      }
    },
    [toggleKnowledgeStatus]
  )
  /**
   * 处理搜索
   */
  // 防抖搜索
  const debouncedSetSearchKeyword = useDebounce((value: string) => {
    setKeyword(value)
  }, 300)
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      debouncedSetSearchKeyword(value)
    },
    [debouncedSetSearchKeyword]
  )

  // 处理创建人筛选
  const handleCreatorChange = useCallback((value: string) => {
    setSelectedCreator(value)
  }, [])

  // 处理对话框关闭
  const handleDialogClose = useCallback((open: boolean) => {
    setDialogOpen(open)
    if (!open) setEditKnowledge(null)
  }, [])

  return (
    <div className="p-6 space-y-6">
      {/* 顶部操作栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Select value={selectedCreator} onValueChange={handleCreatorChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="选择创建人" />
            </SelectTrigger>
            <SelectContent>
              {creators.map((creator) => (
                <SelectItem key={creator.id} value={creator.id}>
                  {creator.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="搜索"
              defaultValue={keyword}
              onChange={handleSearchChange}
              className="px-0 w-[300px] pl-9 pr-2"
            />
          </div>
          <Button
            onClick={handleCreateKnowledge}
            className="flex items-center space-x-2 w-[160px] gradient-primary"
          >
            <Plus className="h-4 w-4" />
            <span>新建</span>
          </Button>
        </div>
      </div>
      {/* 内容区域 */}
      {showLoading ? (
        <div className="flex flex-wrap gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="w-[300px] h-[158px]">
              <Skeleton className="w-full h-full rounded-lg" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-6">
            {filteredKnowledgeList.map((knowledge) => (
              <KnowledgeCard
                key={knowledge.id}
                id={knowledge.id}
                name={knowledge.name}
                creator={knowledge.creator}
                filesNum={knowledge.filesNum}
                docType={knowledge.docType}
                status={knowledge.status}
                onEdit={() => handleEditKnowledge(knowledge)}
                onDelete={handleDeleteKnowledge}
                onToggleStatus={handleToggleStatus}
              />
            ))}
          </div>
          {/* 空状态 */}
          {filteredKnowledgeList.length === 0 && (
            <div className="text-center py-12">
              <EmptyState
                description="暂无知识库，"
                actionText="点击创建"
                onAction={handleCreateKnowledge}
              />
            </div>
          )}
        </>
      )}

      {/* 加载状态指示器 */}
      {(isDeleting || isToggling) && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg">
          <p className="text-sm">操作中...</p>
        </div>
      )}

      <UpdateKnowledgeDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        onSuccess={refresh}
        knowledge={editKnowledge || undefined}
      />
    </div>
  )
}

export default KnowledgeList
